import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import React, {
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from "react";
import { updateSourceQueryCacheWithWords } from "../domain_data";
import { useHover, useKeyPressEvent, useSettings } from "../hooks";
import { copyToClipboard, searchGoogle, searchGoogleTranslate } from "../utils";
import { getUniqueLocationId } from "../fe_types";
import {
  SourceLocation,
  WordView,
  useCreateWordEvent,
  useSetAsGotIt,
  useSetAsIgnored,
  useSetAsLearning,
  useSetAsUnopened,
} from "../api-generated";
import WordPopup from "./WordPopup";
import InlineDef from "./InlineDef";
import Modal from "./Modal";
import CreateTagInstanceForm from "./CreateTagInstanceForm";
import { useCurrentDevice } from "../hooks";
import { useWordContext } from "../context/WordContext";

interface WordProps {
  word: WordView;
  location: SourceLocation;
  isFocused?: boolean;
  isInMultiSelect?: boolean;
  onHover?: () => void;
  onlyFocusOnHover?: boolean;
}

const Word: React.FC<WordProps> = ({
  word,
  location,
  isFocused: isFocusedProp = false,
  onlyFocusOnHover = false,
  isInMultiSelect = false,
  onHover,
}: WordProps) => {
  const wordSpanRef = useRef<HTMLElement>(null);
  const { shouldIgnoreClicks } = useWordContext();

  const { isComp } = useCurrentDevice();
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [showAsMarkedInteresting, setShowAsMarkedInteresting] =
    useState<boolean>(false);
  const [showAsSearched, setShowAsSearched] = useState<boolean>(false);

  useEffect(() => {
    //TODO: maybe should be thinking more about what should happen when word changes...
    //anything else that should be cleaned up?
    setShowAsMarkedInteresting(false);
    setShowAsSearched(false);
  }, [JSON.stringify(location)]);

  const [taggerOptions, setTaggerOptions] = useState<
    | {
        isForToken: boolean; // else is for line/sentence
      }
    | undefined
  >(undefined);
  const { shouldHighlightWords, shouldShowInlineDefs } = useSettings();
  const setAsUnopenedMutation = useSetAsUnopened();
  const setAsLearningMutation = useSetAsLearning();
  const setAsGotItMutation = useSetAsGotIt();
  const setAsIgnoredMutation = useSetAsIgnored();
  const createWordEventMut = useCreateWordEvent();
  const qc = useQueryClient();

  const closePopup = () => setIsPopupOpen(false);

  const mutateStateArgs = { id: word.id };

  const mutateStateOptions = {
    onSuccess: (word: WordView) => {
      updateSourceQueryCacheWithWords([word], qc, location);
      closePopup();
    },
  };

  const eventOptions = {
    onSuccess: () => {
      closePopup();
    },
  };

  const gotIt = () => {
    setAsGotItMutation.mutate(mutateStateArgs, mutateStateOptions);
  };

  const dontGotIt = () => {
    setAsLearningMutation.mutate(mutateStateArgs, mutateStateOptions);
  };

  const sayWhat = () => {
    closePopup();
    createWordEventMut.mutate(
      {
        data: {
          location,
          type: "say_what",
        },
      },
      eventOptions
    );
    setShowAsSearched(true);
  };

  const markAsUnopened = () => {
    setAsUnopenedMutation.mutate(mutateStateArgs, mutateStateOptions);
  };

  const ignore = () => {
    setAsIgnoredMutation.mutate(mutateStateArgs, mutateStateOptions);
  };

  const hmmInteresting = () => {
    createWordEventMut.mutate(
      {
        data: {
          location,
          type: "hmm_interesting",
        },
      },
      eventOptions
    );
    setShowAsMarkedInteresting(true);
  };

  const openTaggerForWord = () => {
    setTaggerOptions({ isForToken: true });
  };

  const openTaggerForSentence = () => {
    setTaggerOptions({ isForToken: false });
  };

  const handleKeyEvent = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "d" && !e.ctrlKey) {
        gotIt();
      } else if (e.key === "s" && !e.ctrlKey) {
        sayWhat();
      } else if (e.key === "y" && !e.ctrlKey) {
        copyToClipboard(word.string);
      } else if (e.key === "t" && !e.shiftKey) {
        openTaggerForWord();
        e.preventDefault();
      } else if (e.key === "T" && e.shiftKey) {
        openTaggerForSentence();
        e.preventDefault();
      } else if (e.key === "m" && !e.ctrlKey) {
        hmmInteresting();
      } else if (e.key === "i" && !e.ctrlKey) {
        ignore();
      } else if (e.key === "l" && !e.ctrlKey) {
        dontGotIt();
      }
    },
    [JSON.stringify(location)]
  );

  const isHovered = useHover(wordSpanRef);

  const isFocused = useMemo(
    () => (onlyFocusOnHover ? isHovered : isFocusedProp),
    [onlyFocusOnHover, isHovered, isFocusedProp]
  );

  const isInterestedInKeyEvents = useMemo(() => isFocused, [isFocused]);

  useEffect(() => {
    if (isHovered && onHover) {
      onHover();
    }
  }, [isHovered]);

  const locationId = useMemo(() => getUniqueLocationId(location), [location]);

  useKeyPressEvent(
    {
      id: locationId,
      priority: 0,
      //priority: EVENT_PRIORITIES.BLOB,
      useCallbackGeneratedCB: handleKeyEvent,
    },
    isInterestedInKeyEvents
  );

  const isChangingState =
    setAsGotItMutation.isLoading ||
    setAsLearningMutation.isLoading ||
    setAsUnopenedMutation.isLoading ||
    setAsIgnoredMutation.isLoading;

  const bgClasses = useMemo(
    () =>
      clsx({
        "bg-amber-400":
          word.status === "is_learning" || word.status === "unopened",
      }),
    [word.status]
  );

  const conditionalBorderClasses = useMemo(() => {
    if (isInMultiSelect) {
      return "border-blue-400";
    }
    if (showAsMarkedInteresting) {
      return "border-pink-400";
    }
    if (showAsSearched) {
      return "border-amber-400";
    }
    if (isFocused) {
      return "border-lime-500";
    }
    return "border-transparent";
  }, [isInMultiSelect, showAsMarkedInteresting, showAsSearched, isFocused]);

  const isEnglishWord = word.language === "ENGLISH";

  const wordTextClasses = clsx([
    "word-text",
    !isEnglishWord && shouldHighlightWords && bgClasses,
    "border border-2",
    conditionalBorderClasses,
    { "animate-pulse": isChangingState },
  ]);

  return (
    <div className="word-container relative">
      <span className="word">
        <button
          className={clsx(`word-button focus:outline-none`, {
            "cursor-pointer": isHovered,
          })}
          onContextMenu={(e) => {
            if (isComp) {
              e.preventDefault();
              setIsPopupOpen((prev) => !prev);
            }
          }}
          onClick={() => {
            console.log("clicked word", word);
            if (!shouldIgnoreClicks) {
              setIsPopupOpen((prev) => !prev);
            }
          }}
          //TODO: this shouldn't be a button if it's English
          disabled={isEnglishWord}
        >
          <span className={wordTextClasses} ref={wordSpanRef}>
            {word.displayString}
          </span>
        </button>
      </span>
      {shouldShowInlineDefs &&
        word.language !== "ENGLISH" &&
        (word.status === "is_learning" ||
          word.status === "unopened" ||
          showAsSearched) && (
          <InlineDef
            word={word}
            location={location}
            onClick={() => {
              if (!shouldIgnoreClicks) {
                setIsPopupOpen(true);
              }
            }}
          />
        )}
      {taggerOptions && (
        <Modal
          //TODO:? not using Modal/Modal.isOpen as it's designed...
          isOpen
          onTriggerClose={() => setTaggerOptions(undefined)}
        >
          <CreateTagInstanceForm
            word={word}
            onSuccessfulTag={() => {
              setTaggerOptions(undefined);
              setIsPopupOpen(false);
            }}
            wordLocation={location}
            isForToken={taggerOptions.isForToken}
          />
        </Modal>
      )}
      {word.language !== "ENGLISH" && isPopupOpen && (
        <WordPopup
          //not using isOpen model since wanna not have the
          //reqs being sent for all the words on a page
          isOpen={true}
          onClose={closePopup}
          location={location}
          word={word}
          onTriggerSearchGoogle={() => searchGoogle(word)}
          onTriggerSearchGoogleTranslate={() => {
            sayWhat();
            searchGoogleTranslate(word);
          }}
          onTriggerGotIt={gotIt}
          onTriggerDontGotIt={dontGotIt}
          onTriggerMarkAsUnopened={markAsUnopened}
          onTriggerIgnore={ignore}
          onTriggerHmmInteresting={hmmInteresting}
          onTriggerSayWhat={sayWhat}
          onTriggerCopyToClipboard={() => {
            copyToClipboard(word.string);
            closePopup();
          }}
          positionStyle={{ top: 0, left: 0 }}
          onTriggerTagWord={openTaggerForWord}
          onTriggerTagSentence={openTaggerForSentence}
        />
      )}
    </div>
  );
};

export default Word;
